import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ShieldAlert } from "lucide-react";

/**
 * Basic authentication route guard
 */
export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-sm text-gray-500 font-medium">Authenticating session...</span>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  return children;
};

/**
 * Role-based route guard
 * Example: <RoleRoute roles={["SUPER_ADMIN", "ADMIN"]}><AdminsManagement /></RoleRoute>
 */
export const RoleRoute = ({ roles = [], children, fallback = null }) => {
  const { user, loading, hasRole } = useAuth();

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  if (!hasRole(...roles)) {
    if (fallback) return fallback;
    return (
      <div className="p-8 max-w-lg mx-auto mt-12 bg-white rounded-2xl shadow-sm border border-red-100 text-center">
        <div className="w-12 h-12 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <ShieldAlert size={26} />
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Access Denied</h2>
        <p className="text-sm text-gray-600 mb-6">
          Your role (<span className="font-semibold text-gray-800">{user.role}</span>) does not have permission to view this module.
        </p>
      </div>
    );
  }

  return children;
};

/**
 * Permission-based route guard
 * Example: <PermissionRoute permission="AGENT_CREATE"><Agent /></PermissionRoute>
 */
export const PermissionRoute = ({ permission, permissions = [], children, fallback = null }) => {
  const { user, loading, hasPermission } = useAuth();

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  const permsToCheck = permission ? [permission] : permissions;
  if (!hasPermission(...permsToCheck)) {
    if (fallback) return fallback;
    return (
      <div className="p-8 max-w-lg mx-auto mt-12 bg-white rounded-2xl shadow-sm border border-amber-100 text-center">
        <div className="w-12 h-12 bg-amber-50 text-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <ShieldAlert size={26} />
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Permission Required</h2>
        <p className="text-sm text-gray-600 mb-6">
          You do not have the required permission(s) to access this feature. Please contact your Super Administrator.
        </p>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;
